import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	PaymentProvider,
	PaymentSystemFrontEndType,
	WithdrawMethod,
} from "../../../../../services/PaymentSystem";
import { useTypedSelector } from "../../../../../redux/store";
import useModelSubscribe from "../../../../../hooks/useModelSubscribe2";
import mapByKey from "../../../../../utils/mapByKey";
import { SettingsTabProps } from "../../../../../components/Settings/tabProps";
import DeleteModal from "../../../../../components/DeleteModal";
import DefaultPageHeader from "../../../../../components/DefaultPageHeader";
import { useTaxiServiceFilterAccessControl } from "../../../../../hooks";
import translationPath from "../constants/translationPath";
import { TabKeys, ARR_ACCESS_PART_KEY } from "../constants/access";

import Root from "./components/Root";
import Modal from "./components/Modal";
import Content from "./components/Content";

const createEmptyTerminalPaymentSystem = (): PaymentSystem.Model =>
	PaymentSystem.fromResponse({
		active: true,
		name: "",
		type: PaymentSystemFrontEndType.TERMINAL_EXECUTOR,
		paymentProvider: PaymentProvider.EASYPAY,
		withdrawMethod: WithdrawMethod.WITHDRAW,
		defaultHoldValue: 0,
		paymentAccounts: [],
		configuration: {
			serviceId: "",
			useSign: false,
			easyPayPublicKey: "",
			providerPrivateKey: "",
			easyPayAction: true,
		},
	});

const TerminalExecutor: React.FC<SettingsTabProps> = () => {
	const { t } = useTranslation();
	const { subTaxiServiceFilterAccess } =
		useTaxiServiceFilterAccessControl<PaymentSystem.Model>();

	const globalLang = useTypedSelector((state) => state.session.language);

	const subscription = useModelSubscribe(
		{
			types: [PaymentSystemFrontEndType.TERMINAL_EXECUTOR],
		},
		PaymentSystem,
	);

	const [showModal, setShowModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [loadingEditModal, setLoadingEditModal] = useState<boolean>(false);
	const [errorTextAll, setErrorTextAll] = useState<string>("");

	const [items, setItems] = useState<PaymentSystem.Model[] | []>([]);

	const modelsAfterAccess = useMemo(() => {
		const data = subscription.models || [];
		return subTaxiServiceFilterAccess({
			models: data,
			branchPath: "taxiServiceIds",
		});
	}, [subscription.models, subTaxiServiceFilterAccess]);

	useEffect(() => {
		setItems(modelsAfterAccess);
	}, [modelsAfterAccess]);

	const [editingItem, setEditingItem] = useState<PaymentSystem.Model>(
		createEmptyTerminalPaymentSystem(),
	);

	const [selected, setSelected] = useState<number[]>([]);

	const modelItemById = useMemo(() => mapByKey(items, "id"), [items]);

	const edit = useCallback(
		(id: number) => {
			const item = modelItemById[id];
			setEditingItem(item);
			setShowModal(true);
			setSelected([]);
		},
		[modelItemById],
	);

	const addHandler = useCallback(() => {
		setEditingItem(createEmptyTerminalPaymentSystem());
		setShowModal(true);
	}, []);

	const editHeaderHandler = useCallback(() => {
		edit(selected[0]);
	}, [edit, selected]);

	const editContentHandler = useCallback(
		(template: PaymentSystem.Modified) => edit(template.id),
		[edit],
	);

	const preDeleteHandler = useCallback(() => {
		setShowDeleteModal(true);
	}, []);

	const deleteHandler = useCallback(() => {
		PaymentSystem.destroy(selected);
		setSelected([]);
		setShowDeleteModal(false);
	}, [selected]);

	const cancelHandler = useCallback(() => {
		setShowModal(false);
		setLoadingEditModal(false);
	}, []);

	const saveHandler = useCallback(
		async (template: PaymentSystem.Modified) => {
			setLoadingEditModal(true);
			const isOtv = template.paymentProvider === PaymentProvider.OTV;
			const isActive =
				typeof template.active === "boolean" ? template.active : null;

			const payload = {
				...template,
				type: PaymentSystemFrontEndType.TERMINAL_EXECUTOR,
				paymentProvider: isOtv
					? PaymentProvider.OTV
					: PaymentProvider.EASYPAY,
				easyPayAction:
					!isOtv && isActive !== null
						? isActive
						: template.easyPayAction,
				otvAction:
					isOtv && isActive !== null ? isActive : template.otvAction,
			};

			if (template.id) {
				const { result, error } = await PaymentSystem.update(payload);
				setErrorTextAll(error ?? "");
				if (result) {
					setEditingItem(createEmptyTerminalPaymentSystem());
					setShowModal(false);
					setErrorTextAll("");
				}
				setLoadingEditModal(false);
				return;
			}

			const { result, error } = await PaymentSystem.create(payload);
			setErrorTextAll(error ?? "");
			if (result) {
				setEditingItem(createEmptyTerminalPaymentSystem());
				setShowModal(false);
				setErrorTextAll("");
			}
			setLoadingEditModal(false);
		},
		[],
	);

	const createdAt = editingItem?.createdAt
		? new Date(editingItem.createdAt).toLocaleDateString()
		: new Date().toLocaleDateString();

	return (
		<Root sizes="auto! 1fr" gaps="16px" maxedWidth maxedHeight>
			<DefaultPageHeader
				canEdit={selected.length === 1}
				canDelete={!!selected.length}
				onAdd={addHandler}
				onDelete={preDeleteHandler}
				onEdit={editHeaderHandler}
				accessName={ARR_ACCESS_PART_KEY[TabKeys.TERMINAL_EXECUTOR]}
			/>
			<Content
				value={items}
				lang={globalLang}
				selected={selected}
				setSelected={setSelected}
				onEdit={editContentHandler}
				saveHandler={saveHandler}
			/>
			{showModal && (
				<Modal
					lang={globalLang}
					onCancel={cancelHandler}
					onSave={saveHandler}
					value={editingItem}
					headerTitle={
						editingItem?.id
							? `${t(`${translationPath}.modal.title.edit`)} "${
									editingItem.name.length > 20
										? `${editingItem.name.slice(0, 20)}...`
										: editingItem.name
							  }"`
							: t(`${translationPath}.modal.title.add`)
					}
					createdAt={createdAt}
					loading={loadingEditModal}
					errorTextAll={errorTextAll}
					setErrorTextAll={setErrorTextAll}
				/>
			)}
			{showDeleteModal && (
				<DeleteModal
					label={
						selected.length === 1
							? t(`${translationPath}.deleteModal.title`) || ""
							: t(`${translationPath}.deleteModal.title2`) || ""
					}
					onCancel={() => {
						setShowDeleteModal(false);
						setSelected([]);
					}}
					onConfirm={deleteHandler}
				/>
			)}
		</Root>
	);
};

export default TerminalExecutor;
